// ==========================================
// pages/Exam/Reading.jsx - FULL FIXED VERSION WITH REAL-TIME ANSWER SAVING
// ==========================================
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AntiCheat from '../../components/AntiCheat/AntiCheat';
import PreExamCheck from '../../components/PreExamCheck/PreExamCheck';
import { formatTime, exitFullscreen } from '../../utils/helpers';
import { 
  Container, Paper, Typography, Radio, RadioGroup, 
  FormControlLabel, Button, LinearProgress, Box, 
  CircularProgress, Divider, IconButton,
  Card, CardContent,
  Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import axios from '../../services/axios';

const Reading = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(55 * 60);
  const [examStarted, setExamStarted] = useState(false);
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [hasOtherTabs, setHasOtherTabs] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [backendResponse, setBackendResponse] = useState(null);
  const [showUnansweredWarning, setShowUnansweredWarning] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);
  
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const [showInstructions, setShowInstructions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: 'success' });
  const [sessionId, setSessionId] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  
  const timerRef = useRef(null);
  const hasSubmittedRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const zoomContainerRef = useRef(null);
  const zoomImageRef = useRef(null);
  const questionRefs = useRef({});
  const saveTimeoutRef = useRef(null);

  const groupedQuestions = useMemo(() => {
    if (!questions || questions.length === 0) return [];
    
    const grouped = [];
    let currentGroup = null;
    
    questions.forEach((q, index) => {
      const passage = q.reading_passage || q.readingPassage || q.passage || '';
      
      if (!currentGroup || currentGroup.passage !== passage) {
        currentGroup = {
          passage: passage,
          questions: []
        };
        grouped.push(currentGroup);
      }
      
      currentGroup.questions.push({
        ...q,
        originalIndex: index,
        id: q.id || q.question_id || `reading_${index + 1}`,
        question: q.question_text || q.question || q.text || q.description || `Reading Question ${index + 1}`,
        image_url: q.image_url || q.imageUrl || q.image || null,
        options: q.options || q.choices || ['A', 'B', 'C', 'D'].map((letter, optIdx) => ({
          value: letter,
          text: `Option ${optIdx + 1}`
        }))
      });
    });

    return grouped;
  }, [questions]);

  // ============================================
  // SHOW SAVE STATUS SNACKBAR
  // ============================================
  const showSaveStatus = (message, type = 'success') => {
    setSaveStatus({ show: true, message, type });
    setTimeout(() => {
      setSaveStatus(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // ============================================
  // SAVE SINGLE ANSWER TO SERVER
  // ============================================
  const saveAnswerToServer = useCallback(async (questionIndex, answer) => {
    if (!autoSaveEnabled || !sessionId) return;
    
    const questionId = questions[questionIndex]?.id;
    if (!questionId) return;

    setSaving(true);
    try {
      const response = await axios.post('/exam/save-answer', {
        session_id: sessionId,
        question_id: questionId,
        user_answer: answer,
        section: 'reading'
      });

      if (response.data.success) {
        setLastSaveTime(new Date());
        // Update question with saved status
        setQuestions(prev => {
          const newQuestions = [...prev];
          if (newQuestions[questionIndex]) {
            newQuestions[questionIndex] = {
              ...newQuestions[questionIndex],
              user_answer: answer,
              has_answered: true
            };
          }
          return newQuestions;
        });
        showSaveStatus('Answer saved successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save answer to server:', error);
      // Fallback: save to localStorage
      localStorage.setItem('reading_answers', JSON.stringify(answers));
      showSaveStatus('Saved locally (offline)', 'warning');
    } finally {
      setSaving(false);
    }
  }, [autoSaveEnabled, sessionId, questions, answers]);

  // ============================================
  // BATCH SAVE ALL ANSWERS TO SERVER
  // ============================================
  const batchSaveAnswers = useCallback(async () => {
    if (!sessionId || Object.keys(answers).length === 0) return;

    const answersArray = Object.entries(answers)
      .map(([index, answer]) => {
        const questionId = questions[parseInt(index)]?.id;
        return questionId ? { question_id: questionId, user_answer: answer } : null;
      })
      .filter(item => item !== null);

    if (answersArray.length === 0) return;

    setSaving(true);
    try {
      const response = await axios.post('/exam/batch-save-answers', {
        session_id: sessionId,
        answers: answersArray,
        section: 'reading',
        time_left: timeLeft
      });

      if (response.data.success) {
        setLastSaveTime(new Date());
        showSaveStatus(`${response.data.saved_count} answers saved`, 'success');
      }
    } catch (error) {
      console.error('Failed to batch save answers:', error);
      // Fallback: save to localStorage
      localStorage.setItem('reading_answers', JSON.stringify(answers));
      showSaveStatus('Saved locally (offline)', 'warning');
    } finally {
      setSaving(false);
    }
  }, [sessionId, answers, questions, timeLeft]);

  // ============================================
  // AUTO-SAVE EVERY 30 SECONDS
  // ============================================
  useEffect(() => {
    if (!autoSaveEnabled || !examStarted || Object.keys(answers).length === 0) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      batchSaveAnswers();
    }, 30000); // Auto-save every 30 seconds

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [answers, autoSaveEnabled, examStarted, batchSaveAnswers]);

  useEffect(() => {
    const listeningCompleted = localStorage.getItem('listening_completed');
    const structureCompleted = localStorage.getItem('structure_completed');
    
    if (listeningCompleted !== 'true' || structureCompleted !== 'true') {
      alert('You must complete Listening and Structure sections first!');
      navigate('/exam/listening', { replace: true });
      return;
    }
    
    // Check for existing session ID
    const existingSessionId = localStorage.getItem('exam_session_id');
    if (existingSessionId) {
      setSessionId(existingSessionId);
    }
    
    const savedTime = localStorage.getItem('reading_time');
    const savedAnswers = localStorage.getItem('reading_answers');
    
    if (savedTime && parseInt(savedTime) > 0) {
      setTimeLeft(parseInt(savedTime));
    }
    
    if (savedAnswers) {
      try {
        setAnswers(JSON.parse(savedAnswers));
      } catch (e) {
        console.error('Error parsing saved answers:', e);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let retryCount = 0;
    const maxRetries = 3;

    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setFetchError(false);
        
        const token = localStorage.getItem('token');
        const headers = token ? { 
          Authorization: `Bearer ${token}`,
          'x-session-id': sessionId 
        } : {};

        const res = await axios.get('/exam/questions/reading', { headers });
        
        setBackendResponse(res.data);

        // Set session ID from response
        const newSessionId = res.data.session_id || res.data.data?.session_id;
        if (newSessionId) {
          setSessionId(newSessionId);
          localStorage.setItem('exam_session_id', newSessionId);
          console.log('✅ Reading Session ID:', newSessionId);
        }

        let loadedQuestions = [];
        
        if (res.data && Array.isArray(res.data.questions)) {
          loadedQuestions = res.data.questions;
        } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
          loadedQuestions = res.data.data;
        } else if (res.data && Array.isArray(res.data)) {
          loadedQuestions = res.data;
        } else {
          console.error('Unexpected response format:', res.data);
          throw new Error('Invalid response format from server');
        }
        
        // Format questions with user answers if available
        const formattedQuestions = loadedQuestions.map((q, idx) => {
          const questionText = q.question_text || q.question || q.text || `Reading Question ${idx + 1}`;
          const passage = q.reading_passage || q.readingPassage || q.passage || '';
          
          return {
            id: q.id || `reading_${idx + 1}`,
            session_id: q.session_id || sessionId,
            question: questionText,
            reading_passage: passage,
            image_url: q.image_url || q.image,
            options: q.options || [
              { value: 'A', text: q.option_a || 'Option A' },
              { value: 'B', text: q.option_b || 'Option B' },
              { value: 'C', text: q.option_c || 'Option C' },
              { value: 'D', text: q.option_d || 'Option D' }
            ],
            user_answer: q.user_answer || null,
            has_answered: !!q.user_answer,
            correct_answer: q.correct_answer,
            raw: q
          };
        });
        
        // Update answers state from loaded questions
        const loadedAnswers = {};
        formattedQuestions.forEach((q, idx) => {
          if (q.user_answer) {
            loadedAnswers[idx] = q.user_answer;
          }
        });
        
        if (Object.keys(loadedAnswers).length > 0) {
          setAnswers(prev => ({ ...prev, ...loadedAnswers }));
        }

        setQuestions(formattedQuestions);
        setLoading(false);
        
      } catch (err) {
        console.error('Error fetching questions:', err);
        
        if (retryCount < maxRetries && err.response?.status !== 401) {
          retryCount++;
          console.log(`Retrying... (${retryCount}/${maxRetries})`);
          setTimeout(fetchQuestions, 3000);
        } else {
          setFetchError(true);
          setLoading(false);
          
          if (err.response?.status === 401) {
            alert('Session expired. Please login again.');
            navigate('/login');
          }
        }
      }
    };

    fetchQuestions();
  }, [navigate, sessionId]);

  const handleImageClick = (imageUrl) => {
    if (!imageUrl) return;
    setZoomedImage(imageUrl);
    setZoomScale(1);
    setZoomPosition({ x: 0, y: 0 });
  };

  const closeZoom = () => {
    setZoomedImage(null);
    setZoomScale(1);
    setZoomPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const zoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoomScale(1);
    setZoomPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    if (!zoomedImage) return;
    e.preventDefault();
    
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (!zoomedImage || zoomScale <= 1) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - zoomPosition.x,
      y: e.clientY - zoomPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !zoomedImage || zoomScale <= 1) return;
    
    e.preventDefault();
    setZoomPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (zoomedImage) {
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('wheel', handleWheel);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [zoomedImage, isDragging, zoomScale, dragStart]);

  const handlePreCheckReady = async () => {
    setShowPreCheck(false);
    const elem = document.documentElement;
    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setExamStarted(true);
      
      // Initial save of any existing answers
      if (Object.keys(answers).length > 0 && sessionId) {
        setTimeout(() => batchSaveAnswers(), 1000);
      }
    } catch (err) {
      console.error('Fullscreen request failed:', err);
      setExamStarted(true);
    }
  };

  const handlePreCheckFail = () => {
    setShowPreCheck(false);
    navigate('/dashboard', { replace: true });
  };

  const startExam = () => {
    if (questions.length === 0) {
      alert('Cannot start exam: Questions not available. Please refresh the page or contact admin.');
      return;
    }
    setShowPreCheck(true);
  };

  // ============================================
  // MANUAL SAVE BUTTON HANDLER
  // ============================================
  const handleManualSave = async () => {
    if (Object.keys(answers).length === 0) {
      showSaveStatus('No answers to save', 'info');
      return;
    }
    
    await batchSaveAnswers();
  };

  // ============================================
  // SUBMIT SECTION HANDLER
  // ============================================
  const handleSubmit = async () => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Final save before submitting
    if (sessionId && Object.keys(answers).length > 0) {
      setSaving(true);
      try {
        await batchSaveAnswers();
        
        // Mark section as completed on server
        await axios.post(`/exam/session/${sessionId}/complete`, {
          section: 'reading'
        });
      } catch (error) {
        console.error('Error finalizing section:', error);
      } finally {
        setSaving(false);
      }
    }

    // Save to localStorage
    localStorage.setItem('reading_answers', JSON.stringify(answers));
    localStorage.setItem('reading_time', timeLeft.toString());
    localStorage.setItem('reading_completed', 'true');
    
    exitFullscreen();
    
    showSaveStatus('Exam completed! Calculating results...', 'success');
    
    setTimeout(() => {
      navigate('/exam/result', { replace: true });
    }, 1000);
  };

  useEffect(() => {
    window.autoSubmitExam = handleSubmit;
    return () => {
      delete window.autoSubmitExam;
    };
  }, [answers, timeLeft, sessionId]);

  useEffect(() => {
    if (!examStarted) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (window.autoSubmitExam) {
            window.autoSubmitExam();
          }
          return 0;
        }
        return prev - 1;
      });
      
      // Auto-save progress every minute
      if (timeLeft % 60 === 0 && autoSaveEnabled && sessionId) {
        batchSaveAnswers();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [examStarted, autoSaveEnabled, sessionId, batchSaveAnswers, timeLeft]);

  // ============================================
  // HANDLE ANSWER SELECTION
  // ============================================
  const handleAnswer = async (questionIndex, value) => {
    const newAnswers = { ...answers, [questionIndex]: value };
    setAnswers(newAnswers);

    // Auto-save to server
    if (autoSaveEnabled) {
      saveAnswerToServer(questionIndex, value);
    }
  };

  const handleFinish = () => {
    const unanswered = [];
    questions.forEach((_, index) => {
      if (!answers[index]) {
        unanswered.push(index);
      }
    });

    if (unanswered.length > 0) {
      setUnansweredCount(unanswered.length);
      setShowUnansweredWarning(true);
      
      if (unanswered.length > 0) {
        const firstUnanswered = unanswered[0];
        if (questionRefs.current[firstUnanswered]) {
          questionRefs.current[firstUnanswered].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }
      return;
    }
    
    handleSubmit();
  };

  const handleForceFinish = () => {
    setShowUnansweredWarning(false);
    handleSubmit();
  };

  const handleViolation = (count) => {
    setViolationCount(count);
    if (count >= 3) {
      alert('You have violated 3 times. Exam will be auto-submitted.');
      handleSubmit();
    }
  };

  const handleTabDetection = (detected) => {
    setHasOtherTabs(detected);
  };

  const hideInstructions = () => {
    setShowInstructions(false);
  };

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" color="#1a3c6d" gutterBottom>
            Loading Reading Questions...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing your exam session...
          </Typography>
          {sessionId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Session ID: {sessionId}
            </Typography>
          )}
        </Paper>
      </Container>
    );
  }

  if (fetchError || questions.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h5" color="error" gutterBottom>
            ❌ Failed to Load Questions
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Unable to fetch reading questions from server.
          </Typography>
          
          {backendResponse && (
            <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                🛠 Debug Information:
              </Typography>
              <Typography variant="caption" component="div">
                <pre style={{ fontSize: '10px', overflow: 'auto', maxHeight: '200px' }}>
                  {JSON.stringify(backendResponse, null, 2)}
                </pre>
              </Typography>
            </Box>
          )}
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" textAlign="left">
              Possible causes:<br />
              • Data format from backend does not match<br />
              • Connection to server interrupted<br />
              • Questions not yet available in database<br />
              • Login session expired
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ bgcolor: '#1a3c6d', mr: 2, mb: 2 }}
          >
            🔄 Refresh Page
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  if (showPreCheck) {
    return (
      <PreExamCheck 
        onReady={handlePreCheckReady}
        onFail={handlePreCheckFail}
      />
    );
  }

  if (!examStarted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="#1a3c6d" gutterBottom>
            Reading Comprehension
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            {questions.length} questions • 55 minutes
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Questions successfully loaded ({questions.length} questions)
          </Alert>
          
          {sessionId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              📝 Session ID: <strong>{sessionId}</strong>
            </Alert>
          )}
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" fontWeight="bold" gutterBottom>
              ⚠ STRICT RULES
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="left">
              • Exam will run in fullscreen mode<br />
              • Do not open other tabs/applications<br />
              • Do not use keyboard shortcuts<br />
              • 3x violations = auto submit<br />
              • All questions displayed on 1 page<br />
              • 🔍 Click images to zoom<br />
              • <strong>Auto-save enabled</strong> - answers saved automatically
            </Typography>
          </Box>

          <Typography variant="body2" color="#1a3c6d" sx={{ mb: 3, fontWeight: 'bold' }}>
            📖 Final section! Complete it well.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={startExam}
            sx={{ bgcolor: '#1a3c6d', px: 6, py: 2, fontSize: 18 }}
          >
            Start Pre-Check
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <AntiCheat 
        onViolation={handleViolation}
        onTabDetected={handleTabDetection}
      />
      
      <Dialog
        open={showUnansweredWarning}
        onClose={() => setShowUnansweredWarning(false)}
        aria-labelledby="unanswered-warning-title"
      >
        <DialogTitle id="unanswered-warning-title" sx={{ color: 'warning.main' }}>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Unanswered Questions
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You have <strong>{unansweredCount} unanswered question(s)</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to continue? You can go back to answer them, or submit the exam as is.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowUnansweredWarning(false)}
            variant="outlined"
          >
            Go Back and Answer
          </Button>
          <Button 
            onClick={handleForceFinish}
            variant="contained"
            color="primary"
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 3, 
            position: 'sticky', 
            top: 0, 
            zIndex: 1000,
            bgcolor: 'white',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Box sx={{ mb: { xs: 2, sm: 0 } }}>
              <Typography variant="h5" fontWeight="bold">Reading Comprehension</Typography>
              <Typography variant="body2" color="text.secondary">
                Answered: {answeredCount}/{questions.length} questions
              </Typography>
              {sessionId && (
                <Typography variant="caption" color="text.secondary">
                  Session: {sessionId}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              <Typography variant="h6">
                Time: <strong style={{ color: timeLeft < 300 ? '#e74c3c' : '#1a3c6d' }}>
                  {formatTime(timeLeft)}
                </strong>
              </Typography>
              {violationCount > 0 && (
                <Typography variant="caption" color="error" display="block">
                  ⚠ Violations: {violationCount}/3
                </Typography>
              )}
              {hasOtherTabs && (
                <Typography variant="caption" sx={{ color: '#ff9800' }} display="block">
                  ⚠ Other tabs detected!
                </Typography>
              )}
              {lastSaveTime && (
                <Typography variant="caption" sx={{ color: '#2e7d32' }} display="block">
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Saved: {lastSaveTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                flex: 1, 
                height: 8, 
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progress >= 80 ? '#2e7d32' : '#1a3c6d'
                }
              }} 
            />
            <Button
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleManualSave}
              disabled={saving || Object.keys(answers).length === 0}
              variant="outlined"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="small"
              color={autoSaveEnabled ? 'success' : 'default'}
              variant={autoSaveEnabled ? 'contained' : 'outlined'}
              onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
            >
              {autoSaveEnabled ? 'Auto-save ON' : 'Auto-save OFF'}
            </Button>
          </Box>
        </Paper>

        {showInstructions && (
          <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa', border: '2px solid #1a3c6d' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#1a3c6d" gutterBottom>
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  INSTRUCTIONS - READING COMPREHENSION
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Official TOEFL Reading Comprehension Test Instructions
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                onClick={hideInstructions}
                sx={{ borderColor: '#1a3c6d', color: '#1a3c6d' }}
              >
                Close
              </Button>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Card variant="outlined" sx={{ borderLeft: '4px solid #3b82f6' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" color="#3b82f6" gutterBottom>
                    Directions: Reading Comprehension Section
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                    In this section you will read several passages. Each one is followed by a number of questions about it. 
                    You are to choose the one best answer, (A), (B), (C), or (D), to each question. Then, on your answer sheet, 
                    find the number of the question and fill in the space that corresponds to the letter of the answer you have chosen.
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'justify', mt: 2, fontStyle: 'italic' }}>
                    Answer all questions about the information in a passage on the basis of what is stated or implied in that passage.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <strong>TIPS:</strong> Read the passage carefully before answering questions. The given time is sufficient to read and understand each passage.
            </Alert>
          </Paper>
        )}

        {!showInstructions && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setShowInstructions(true)}
              startIcon={<InfoIcon />}
              sx={{ borderColor: '#1a3c6d', color: '#1a3c6d' }}
            >
              Show Instructions
            </Button>
          </Box>
        )}

        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, userSelect: 'none' }}>
          {groupedQuestions.map((group, groupIndex) => (
            <Box key={groupIndex} sx={{ mb: 6 }}>
              {group.passage && group.passage.trim() !== '' && (
                <Box sx={{ 
                  mb: 4,
                  bgcolor: 'white', 
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  fontFamily: "'Times New Roman', Times, serif",
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <Box sx={{
                    p: '12px 20px',
                    bgcolor: '#f0f4f8',
                    borderBottom: '2px solid #1a3c6d'
                  }}>
                    <Typography sx={{ 
                      color: '#1a3c6d', 
                      fontWeight: 'bold',
                      fontSize: '16px',
                      m: 0
                    }}>
                      Reading Passage {groupIndex + 1}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex',
                    height: '500px',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      width: '45px',
                      overflowY: 'auto',
                      bgcolor: '#f9f9f9',
                      borderRight: '1px solid #d1d5db',
                      fontFamily: "'Courier New', monospace",
                      fontSize: '12px',
                      color: '#555',
                      flexShrink: 0
                    }}>
                      <Box sx={{ p: '16px 0' }}>
                        {Array.from({ length: 60 }, (_, i) => {
                          const lineNum = i + 1;
                          return (
                            <Box
                              key={lineNum}
                              sx={{
                                height: '1.8em',
                                lineHeight: '1.8em',
                                textAlign: 'right',
                                pr: '10px',
                                borderRight: lineNum % 5 === 0 ? '2px solid #1a3c6d' : 'none',
                                fontWeight: lineNum % 5 === 0 ? 'bold' : 'normal',
                                color: lineNum % 5 === 0 ? '#1a3c6d' : '#777'
                              }}
                            >
                              {lineNum}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                    
                    <Box sx={{
                      flex: 1,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      p: '16px 24px',
                      fontSize: '15px',
                      lineHeight: '1.8',
                      color: '#000',
                      textAlign: 'justify'
                    }}>
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {group.passage}
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box sx={{
                    p: '8px 20px',
                    bgcolor: '#f8f9fa',
                    borderTop: '1px solid #e9ecef',
                    fontSize: '12px',
                    color: '#6c757d',
                    textAlign: 'center'
                  }}>
                    Line numbers appear on the left • Scroll to read full passage
                  </Box>
                </Box>
              )}

              {group.questions.map((q, qIndex) => (
                <Box 
                  key={q.id || q.originalIndex}
                  ref={el => questionRefs.current[q.originalIndex] = el}
                  sx={{ 
                    mb: 4, 
                    pb: 3, 
                    borderBottom: qIndex < group.questions.length - 1 ? '1px solid #e0e0e0' : 'none',
                    position: 'relative'
                  }}
                >
                  {q.has_answered && (
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: '#2e7d32',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      zIndex: 1
                    }}>
                      <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      Saved
                    </Box>
                  )}
                  
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: '#1a3c6d', 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{ 
                      width: 30, 
                      height: 30, 
                      borderRadius: '50%', 
                      bgcolor: answers[q.originalIndex] ? '#4caf50' : '#e0e0e0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}>
                      {q.originalIndex + 1}
                    </Box>
                    Question {q.originalIndex + 1}
                    {answers[q.originalIndex] && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#2e7d32', fontWeight: 'bold' }}
                      >
                        ✓ Answered
                      </Typography>
                    )}
                  </Typography>
                  
                  {q.image_url && (
                    <Box sx={{ 
                      mb: 3, 
                      textAlign: 'center',
                      position: 'relative'
                    }}>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'inline-block',
                          cursor: 'zoom-in',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            boxShadow: '0 6px 20px rgba(59, 130, 246, 0.2)',
                            '& .zoom-indicator': {
                              opacity: 1
                            }
                          }
                        }}
                        onClick={() => handleImageClick(q.image_url)}
                      >
                        <img 
                          src={q.image_url} 
                          alt={`Question ${q.originalIndex + 1}`}
                          style={{ 
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        
                        <Box
                          className="zoom-indicator"
                          sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            pointerEvents: 'none'
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 14 }} />
                          ZOOM
                        </Box>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        🔍 Click image to zoom
                      </Typography>
                    </Box>
                  )}

                  <Typography paragraph sx={{ 
                    mb: 3, 
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    bgcolor: '#f9f9f9',
                    p: 2,
                    borderRadius: 2
                  }}>
                    {q.question}
                  </Typography>

                  <RadioGroup 
                    value={answers[q.originalIndex] || ''} 
                    onChange={(e) => handleAnswer(q.originalIndex, e.target.value)}
                    sx={{ ml: 1 }}
                  >
                    {q.options.map((opt, i) => (
                      <FormControlLabel 
                        key={opt.value || i}
                        value={opt.value} 
                        control={<Radio color="primary" />} 
                        label={
                          <Typography sx={{ 
                            fontSize: '1rem',
                            color: answers[q.originalIndex] === opt.value ? '#1a3c6d' : 'inherit',
                            fontWeight: answers[q.originalIndex] === opt.value ? 'bold' : 'normal'
                          }}>
                            <strong>{opt.value}.</strong> {opt.text}
                          </Typography>
                        }
                        sx={{ 
                          mb: 1.5, 
                          cursor: 'pointer !important',
                          '&:hover': {
                            bgcolor: '#f0f7ff',
                            borderRadius: 2
                          },
                          p: 1.5,
                          border: answers[q.originalIndex] === opt.value ? '1px solid #1a3c6d' : '1px solid transparent',
                          borderRadius: 2,
                          transition: 'all 0.2s'
                        }} 
                      />
                    ))}
                  </RadioGroup>
                </Box>
              ))}
            </Box>
          ))}

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleFinish}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{ 
                bgcolor: '#1a3c6d', 
                px: 8, 
                py: 2,
                fontSize: 18,
                cursor: 'pointer !important',
                '&:hover': {
                  bgcolor: '#0d2b5c',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s',
                boxShadow: 3,
                '&:disabled': {
                  bgcolor: '#9ca3af'
                }
              }}
            >
              {saving ? 'Saving...' : 'View Results 🎯'}
            </Button>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              All answers are automatically saved. Last save: {lastSaveTime ? lastSaveTime.toLocaleTimeString() : 'Never'}
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Save Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={saveStatus.type} sx={{ width: '100%' }}>
          {saveStatus.message}
        </Alert>
      </Snackbar>

      {zoomedImage && (
        <Box
          ref={zoomContainerRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.95)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: zoomScale > 1 ? 'grab' : 'zoom-out',
            '&:active': {
              cursor: zoomScale > 1 ? 'grabbing' : 'zoom-out'
            }
          }}
          onClick={(e) => {
            if (e.target === zoomContainerRef.current) {
              closeZoom();
            }
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              display: 'flex',
              gap: '10px',
              zIndex: 10000
            }}
          >
            <IconButton
              onClick={zoomIn}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' }
              }}
              title="Zoom In"
            >
              <ZoomInIcon />
            </IconButton>
            
            <IconButton
              onClick={zoomOut}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' }
              }}
              title="Zoom Out"
            >
              <ZoomOutIcon />
            </IconButton>
            
            <IconButton
              onClick={resetZoom}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' }
              }}
              title="Reset Zoom"
            >
              <FullscreenIcon />
            </IconButton>
            
            <IconButton
              onClick={closeZoom}
              sx={{
                bgcolor: 'rgba(220, 53, 69, 0.9)',
                color: 'white',
                '&:hover': { bgcolor: '#dc3545' }
              }}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: '30px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.7)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              display: 'flex',
              gap: '15px',
              alignItems: 'center',
              zIndex: 10000
            }}
          >
            <span>🔍 Zoom: {Math.round(zoomScale * 100)}%</span>
            <span>•</span>
            <span>🖱 Scroll to zoom</span>
            <span>•</span>
            <span>🧿 Drag to pan (when zoomed)</span>
            <span>•</span>
            <span>❌ Click outside to close</span>
          </Box>

          <Box
            sx={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              transform: `scale(${zoomScale}) translate(${zoomPosition.x}px, ${zoomPosition.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease',
              cursor: zoomScale > 1 ? 'grab' : 'default'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              ref={zoomImageRef}
              src={zoomedImage}
              alt="Zoomed"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '8px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                display: 'block'
              }}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect width="400" height="300" fill="%23f0f0f0"/><text x="200" y="150" text-anchor="middle" fill="%23999" font-family="Arial" font-size="16">Image cannot be loaded</text></svg>';
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default Reading;
